<template>
  <div>
    <Spinner v-if="pageDataLoading" />

    <div class="row no-gutters">
      <div class="col-2 pr-1">
        <div class="agent-view-container">          
          <ContentTitle>Agents</ContentTitle>

          <div class="mb-1" v-for="agent of agentList" :key="agent.id">
            <AIAgentPanel :agent="agent" @click="selectAgent(agent)" :selected="agent.id == selectedAgentId" />
          </div>
        </div>
      </div>

      <div class="col-10 pl-1">
        <ContentTitle v-if="hasSelectedAgent">{{selectedAgent.name}} - {{selectedAgent.title}}</ContentTitle>
        <ContentTitle v-if="!hasSelectedAgent">Synergy AI</ContentTitle>

        <div class="empty-container" v-if="!hasSelectedAgent">
          <div class="empty-background"></div>
          <div class="row h-100">
            <div class="col-6 offset-3 my-auto text-center empty-background-content">
              <h1>Welcome to Synergy AI</h1>
              <p class="text-muted font-italic">Select an agent from the left to view conversations and reports.</p>
            </div>
          </div>
        </div>

        <ContentBox v-if="hasSelectedAgent">
          <template #content>
            <div class="agent-view-container">
              <div class="row">
                <div class="col-12">
                  <div class="agent-bar">
                    <div class="agent-avatar">
                      <Avatar :name="selectedAgent.name" :image="agentProfilePicURL" :editable="false" :size="85" />
                    </div>
                    <div class="agent-info">
                      <ContentTitle>{{selectedAgent.name}}</ContentTitle>
                      <ContentSubTitle class="mb-1">{{selectedAgent.title}}</ContentSubTitle>
                      <span class="text-muted">Hi, I&apos;m {{selectedAgent.name}}, your {{selectedAgent.title}}.<br />{{selectedAgent.description}}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div class="row no-gutters">
                <div class="col-2 sidebar">
                  <div class="conversation-list-container">
                    <div class="conversation-list scroller-thin">
                      <ContentTitle>Reports</ContentTitle>
                      <AIReportPanel v-for="report of agentReportList" :key="report.id" :class="['conversation-item']" @click.prevent="selectReport(report)" :agent="selectedAgent" :report="report">
                      </AIReportPanel>
                      <div v-if="agentReportList.length == 0">
                        <div class="text-muted font-italic">No reports available</div>
                      </div>

                      <br /><br />
                      <ContentTitle>Recent Conversations</ContentTitle>
                      <div v-for="conv of conversationList" :key="conv.id" :class="['row', 'no-gutters', 'conversation-item', { selected: conv.id == selectedConversationId }]" @click.prevent="selectConversation(conv)">
                        <div class="col-10">
                          <div class="title">{{conv.title}}</div>
                          <div class="date text-muted">{{formatDateTime(conv.createdAt)}}</div>
                        </div>
                        <div class="col-2">
                          <ActionDropMenu :tableAction="true" :forceDropdown="true">
                            <DropdownMenuItem @click="deleteConversationClicked(conv)" icon="fa-trash-alt">Delete</DropdownMenuItem>
                          </ActionDropMenu>
                        </div>
                      </div>
                      <div v-if="conversationList.length == 0">
                        <div class="text-muted font-italic">No recent conversations<br />Click on a report above to start</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-10">
                  <div class="agent-area">
                    <AIConversationChat :agent="selectedAgent" :conversationId="selectedConversationId" v-if="hasSelectedConversation" />
                    
                    <div class="empty-container" v-if="!hasSelectedConversation && selectedReport">

                      <div class="row mt-4">
                        <div class="col-6 offset-3 text-center">

                          <AIReportPreview :report="selectedReport" :agent="selectedAgent" @startReport="startReport(selectedReport)" v-if="!startingReport">
                          </AIReportPreview>

                          <div v-if="startingReport">
                            <Spinner />
                            <p class="text-muted font-italic mt-2">Starting report...</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="empty-container" v-if="hasSelectedAgent && !hasSelectedConversation && !selectedReport">
                      <div class="empty-background"></div>
                      <div class="row h-100">
                        <div class="col-6 offset-3 my-auto text-center">
                          <p class="text-muted font-italic">Select a report or existing conversation on the left to get started.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </ContentBox>
      </div>
    </div>

  </div>
</template>


<style lang="scss" scoped>
$agent-header-size: 90px;

.agent-view-container {
  min-height: 75vh;
}
.sidebar {
  border-right: 1px solid #e7e7e7;
}
.conversation-list-container {
  position: relative;
  height: 100%;
}
.conversation-list {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow-y: auto;
  padding: 0 10px;
}
.agent-list {
  border-right: 1px solid #e7e7e7;
}
.agent-area {
  height: 68dvh;
}

.conversation-item {
  border: 1px solid #e7e7e7;
  border-radius: $layout-minor-border-radius;
  margin-bottom: 6px;
  background: #EBE8E4;
  padding: 4px;

  cursor: pointer;
}
.conversation-item.selected {
  background: #f0f0f0;
  border-color: #c0c0c0;
}
.conversation-item .title {
  font-weight: bold;
}
.conversation-item .date {
  font-size: 11px;
}

.agent-bar {
  height: $agent-header-size;
  border-bottom: 1px solid #e7e7e7;
  display: flex;
  flex-direction: row;
}
.agent-bar .agent-avatar {
  width: 80px;
}
.agent-bar .agent-info {
  padding-left: 10px;
  flex: 1;
}

.empty-container {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 60vh;
}
.empty-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;  

  opacity: 0.5;
  background-image: radial-gradient(circle, #50505c 1.44px, transparent 1.44px);
  background-size: 18px 18px;
  mask-image: radial-gradient(ellipse at center, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0) 75%);
  -webkit-mask-image: radial-gradient(ellipse at center, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0) 75%);
  mask-size: 100% 100%;
  -webkit-mask-size: 100% 100%;
  mask-repeat: no-repeat;
  -webkit-mask-repeat: no-repeat;
}
.empty-background-content {
  padding: 5px;
  background: #EBE8E4;
  border-radius: $layout-minor-border-radius;  
}
</style>


<script>
import Avatar from '@/components/avatar.vue'
import AIConversationChat from '@/modules/ai/components/aiConversationChat'
import AIAgentPanel from '@/modules/ai/components/aiAgentPanel'
import AIReportPanel from '@/modules/ai/components/aiReportPanel'
import AIReportPreview from '@/modules/ai/components/aiReportPreview'

export default {
  name: 'AgentView',
  components: {
    Avatar,
    AIConversationChat,
    AIAgentPanel,
    AIReportPanel,
    AIReportPreview
  },
  data() {
    return {
      selectedAgentId: null,
      selectedConversationId: null,
      conversationList: [],

      selectedAgent: null,
      selectedReport: null,
      agentList: [],

      startingReport: false
    }
  },
  computed: {
    agentProfilePicURL() {
      if(this.selectedAgent)
        return `/ai/images/${this.selectedAgent.avatarType}.png`
      return '';
    },
    hasSelectedAgent() {
      return this.selectedAgentId && this.selectedAgent != null
    },
    hasSelectedConversation() {
      return this.selectedConversationId != null
    },
    agentReportList() {
      return this.selectedAgent.aiAgentReports.filter(report => report.isActive)
    },
    profileCompanyName() {
      return this.$store.state.profile.profileCompanyName
    },
  },
  watch: {
    selectedAgentId: function() {
      this.requestAgentData()
    }
  },
  mounted() {
    this.requestData()
  },
  methods: {
    requestData() {
      this.pageLoadData(this.$apiService.aiService.getAgentList())
      .then(response => {
        this.agentList = response.data.filter(agent => agent.isActive)
      })
      .catch(err => this.handlePageLoadError(err))
    },
    requestAgentData() {
      this.selectedAgent = null
      this.selectedConversationId = null
      this.conversationList = []

      if(!this.selectedAgentId)
        return

      Promise.all([
        this.$apiService.aiService.getAgent(this.selectedAgentId),
        this.$apiService.aiService.getAgentConversationList(this.selectedAgentId),
      ])
      .then(results => {
        this.selectedAgent = results[0].data
        this.conversationList = results[1].data
      })
      .catch(err => this.handlePageLoadError(err))
    },
    reloadConversationList() {
      this.$apiService.aiService.getAgentConversationList(this.selectedAgentId)
      .then(response => {
        this.conversationList = response.data
      })
      .catch(err => {
        console.error(err)
      })
    },
    selectConversation(conversation) {
      this.selectedConversationId = conversation.id
      this.selectedReport = null
    },
    deleteConversationClicked(conversation) {
      this.confirmDeleteMessage('Delete Conversation?', 'Are you sure you want to delete this conversation?')
      .then(confirmed => {
        if(confirmed) {
          this.$apiService.aiService.deleteConversation(conversation.id)
          .then(response => {
            // Reload
            this.requestAgentData()
          })
          .catch(err => {
            this.displayNetworkError(err, 'Error deleting conversation')
          })
        }
      })
    },
    selectAgent(agent) {
      this.selectedAgentId = agent.id
      this.selectedReport = null
    },
    selectReport(report) {
      this.selectedConversationId = null
      this.selectedReport = report
    },
    startReport(report) {
      this.startingReport = true

      this.$apiService.aiService.createConversation(this.selectedAgentId, { aiAgentReportId: report.id })
      .then(response => {

        // Intentiionally add a delay before triggering the conversation
        // We do this as the conversation streaming is done via the Reporting API, which uses replication to get the conversation data to the Reporting API database.
        // This means there can be a delay between when the conversation is created and when the conversation data is available to be streamed.
        // Bit hacky, but this is the simplest way to handle this without adding additional complexity around waiting for the conversation to be available for streaming.
        let replicationDelay = 1500 // 1.5 seconds, adjust as needed based on observed replication times

        setTimeout(() => {
          this.selectedConversationId = response.data.id

          // Reload conversation list
          this.reloadConversationList()

          this.startingReport = false
        }, replicationDelay)
      })
      .catch(err => {
        console.error(err)
        this.displayNetworkError(err, 'Error starting report')

        this.startingReport = false
      })
    }
  }
}
</script>